import vertexTextureQuad from "./quad.vert.wgsl";
import fxaaShader from "./fxaa.wgsl";
import { getBlendShader } from "./BlendShader";
import { BlendSettings, UnProjectSettings } from "./BlendSettings";
import { USE_WBOIT } from "../../globals";

/** @import { Renderer } from '../Renderer' */
/** @import { TimingManager } from '../TimingManager'; */

/**
 * @param {Renderer} renderer
 */
export function BlendPass(renderer) {

  /** @type {Renderer} */
  let _renderer = renderer;
  /** @type {GPUDevice} */
  let _device;

  let _presentationFormat = navigator.gpu.getPreferredCanvasFormat();

  let _blendSettings = new BlendSettings();
  let _unProjectSettings = new UnProjectSettings();
  let _blendPipelines = [null, null, null, null];
  let _fxaaPipeline;
  let _blendBindGroupLayout;
  let _fxaaBindGroupLayout;
  let _blendPassDescriptor;

  let _blendTexBindGroup;
  let _postBindGroups = [];
  let _postTargetViews = [];
  let _sampler;

  let _spatialFilterEnabled = false;

  /** @type {TimingManager|undefined} */
  let _timingManager;

  this.init = function () {
    _device = _renderer.getDevice();
    _timingManager = _renderer.getTimingManager();
    _unProjectSettings.init(_device);
    _blendSettings.init(_device);
  };

  this.getBlendSettings = function () {
    return _blendSettings;
  };

  function createPostTargets() {
    for (let i = 0; i < 2; i++) {

      const postTarget = _renderer.getRenderTargets().getPostTarget(i);
      _postTargetViews[i] = postTarget.createView();
      _postTargetViews[i].label = `view:${postTarget.label}`;

      _postBindGroups[i] = _device.createBindGroup({
        layout: _fxaaBindGroupLayout,
        entries: [
        { binding: 0, resource: _postTargetViews[i] },
        { binding: 1, resource: _sampler }]

      });
    }
  }

  function createFXAAPass() {
    if (!_fxaaBindGroupLayout) {
      _fxaaBindGroupLayout = _device.createBindGroupLayout({
        entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: {} }]

      });
    }

    if (!_fxaaPipeline) {
      _fxaaPipeline = _device.createRenderPipeline({
        layout: _device.createPipelineLayout({
          bindGroupLayouts: [_fxaaBindGroupLayout]
        }),
        vertex: {
          module: _device.createShaderModule({
            label: 'fxaa vertex shader',
            code: vertexTextureQuad
          }),
          entryPoint: 'mainFlipY'
        },
        fragment: {
          module: _device.createShaderModule({
            label: 'fxaa fragment shader',
            code: fxaaShader
          }),
          entryPoint: 'FxaaPixelShader',
          targets: [
          {
            format: _presentationFormat
          }]

        },
        primitive: {
          topology: 'triangle-list',
          cullMode: 'back'
        }
      });
    }

    if (!_sampler) {
      _sampler = _device.createSampler({
        magFilter: 'linear',
        minFilter: 'linear'
      });
    }

  }

  /**
   *
   * @param {Boolean} enableSpatialFilter - rollover highlighting for Levels enabled
   * @param {Boolean} toPost - blend pass is writing to another target (not directly to screen)
   * @returns {GPURenderPipeline}
   * @private
   */
  function _createBlendPipeline(enableSpatialFilter, toPost) {
    const blendSettingsLayout = _blendSettings.getLayout();
    const unProjectSettingsLayout = _unProjectSettings.getLayout();
    blendSettingsLayout.label = 'blend-pass-settings';
    unProjectSettingsLayout.label = 'blend-pass-unproject';

    const categoryHighlightingEnabled = _renderer.isCategoryHighlightingEnabled();
    const categoryHighlightingFallback = _renderer.isCategoryHighlightingFallback();

    return _device.createRenderPipeline({
      label: `blend-pass`,
      layout: _device.createPipelineLayout({
        bindGroupLayouts: [
        _blendBindGroupLayout,
        blendSettingsLayout,
        unProjectSettingsLayout]

      }),
      vertex: {
        module: _device.createShaderModule({ label: 'blend-vertex-shader', code: vertexTextureQuad }),
        entryPoint: "main"
      },
      fragment: {
        module: _device.createShaderModule({ label: 'blend-fragment-shader', code: getBlendShader(enableSpatialFilter, USE_WBOIT, categoryHighlightingEnabled, categoryHighlightingFallback) }),
        entryPoint: "main",
        targets: [{ format: toPost ? _renderer.getRenderTargets().getColorTargetFormat() : _renderer.getRenderTargets().getPreferredFormat() }]
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'back'
      }
    });
  }

  /**
   *
   * @param {Boolean} enableSpatialFilter - rollover highlighting for Levels enabled
   * @param {Boolean} toPost - blend pass is writing to another target (not directly to screen)     * @returns {*|GPURenderPipeline}
   */
  function getBlendPipeline(enableSpatialFilter, toPost) {
    const whichPipeline = enableSpatialFilter | toPost << 1;
    let res = _blendPipelines[whichPipeline];

    if (res) {
      return res;
    }

    _blendPipelines[whichPipeline] = res = _createBlendPipeline(enableSpatialFilter, toPost);

    return res;
  }

  function createBlendPass() {

    if (!_blendBindGroupLayout) {
      let entries = [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } }, // color-target
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } }, // overlay-target
      { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'uint' } }, // id-target-0
      { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'uint' } }, // id-target-1
      { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'uint' } }, // id-target-2 -> category-id
      { binding: 5, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } }, // ao-target
      { binding: 6, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth' } }, // depth-target
      { binding: 7, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } }, // wboit-accumulation-target
      { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } } // wboit-revealage-target
      ];

      // Remove tID3 binding if category highlighting is disabled OR if using fallback mode
      // (fallback packs category into modelId, so no separate tID3 texture)
      const removeCategoryIdTarget = !_renderer.isCategoryHighlightingEnabled() || _renderer.isCategoryHighlightingFallback();
      if (removeCategoryIdTarget) {
        entries = entries.filter((entry) => entry.binding !== 4);
      }
      const removeWboitTargets = !USE_WBOIT;
      if (removeWboitTargets) {
        entries = entries.filter((entry) => entry.binding !== 7 && entry.binding !== 8);
      }
      _blendBindGroupLayout = _device.createBindGroupLayout({ entries });
    }

    // All entries need to be recreated when render targets change size
    const rt = _renderer.getRenderTargets();
    // Map target views to entries (bindings 0-4, with 4 conditional)
    let entries = rt.getTargetViewsForBlend().map((resource, binding) => ({ binding, resource }));

    // tAO and tDepth always at fixed bindings 5 and 6 (binding 4 may be skipped)
    entries.push({ binding: 5, resource: _renderer.getSAO().getTargetView() });
    entries.push({ binding: 6, resource: rt.getDepthTargetForSampling().createView() });

    if (USE_WBOIT) {
      entries.push(
        { binding: 7, label: `wboit-accumulation-view`, resource: rt.getWboitAccumViewForSampling() },
        { binding: 8, label: `wboit-revealage-view`, resource: rt.getWboitRevealageViewForSampling() }
      );
    }

    _blendTexBindGroup = _device.createBindGroup({
      label: `blend-pass-textures`,
      layout: _blendBindGroupLayout,
      entries
    });

    //console.log(_blendTexBindGroup);

    if (!_blendPassDescriptor) {
      _blendPassDescriptor = {
        colorAttachments: [
        {
          // view is acquired and set in render loop.
          view: undefined,

          clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 },
          loadOp: 'clear',
          storeOp: 'store'
        }]


      };
    }
  }

  this.resize = function () {
    createFXAAPass();
    createBlendPass();
    createPostTargets();
  };

  this.run = function (targetView, antialias, camera) {
    _blendSettings.upload();

    if (_spatialFilterEnabled) {
      _unProjectSettings.setCamera(camera);
      _unProjectSettings.upload();
    }

    let commandEncoder = _device.createCommandEncoder();

    if (!antialias) {
      _blendPassDescriptor.colorAttachments[0].view = targetView;
      // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
      // nosemgrep
      _blendPassDescriptor.label = 'blend pass (direct)';
      if (_timingManager) {
        _timingManager.newPassQuery(_blendPassDescriptor);
      }

      //Blend pass directly into screen target
      const pass = commandEncoder.beginRenderPass(_blendPassDescriptor);
      pass.setPipeline(getBlendPipeline(_spatialFilterEnabled, false));
      pass.setBindGroup(0, _blendTexBindGroup);
      pass.setBindGroup(1, _blendSettings.getBindGroup());
      pass.setBindGroup(2, _unProjectSettings.getBindGroup());
      pass.draw(3);
      pass.end();
    } else {

      _blendPassDescriptor.colorAttachments[0].view = _postTargetViews[0];
      // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
      // nosemgrep
      _blendPassDescriptor.label = 'blend pass (to-post0)';
      if (_timingManager) {
        _timingManager.newPassQuery(_blendPassDescriptor);
      }

      //Blend pass into Post0 target
      const pass1 = commandEncoder.beginRenderPass(_blendPassDescriptor);
      pass1.setPipeline(getBlendPipeline(_spatialFilterEnabled, true));
      pass1.setBindGroup(0, _blendTexBindGroup);
      pass1.setBindGroup(1, _blendSettings.getBindGroup());
      pass1.setBindGroup(2, _unProjectSettings.getBindGroup());
      pass1.draw(3);
      pass1.end();

      // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
      // nosemgrep
      _blendPassDescriptor.label = 'blend pass (post0-to-screen)';
      if (_timingManager) {
        _timingManager.newPassQuery(_blendPassDescriptor);
      }

      //FXAA from Post0 to screen
      _blendPassDescriptor.colorAttachments[0].view = targetView;
      const pass2 = commandEncoder.beginRenderPass(_blendPassDescriptor);
      pass2.setPipeline(_fxaaPipeline);
      pass2.setBindGroup(0, _postBindGroups[0]);
      pass2.draw(3);
      pass2.end();
    }

    // _blendSettings.copyBufferToRead(commandEncoder);


    _device.queue.submit([commandEncoder.finish()]);
  };

  this.enableSpatialFilter = function () {
    _spatialFilterEnabled = true;
  };

  this.disableSpatialFilter = function () {
    _spatialFilterEnabled = false;
  };

  /**
   * Resets all cached pipelines and bind group layouts. Should be called when
   * category highlighting state changes to force recreation with correct configuration.
   */
  this.resetPipelines = function () {
    _blendPipelines = [null, null, null, null];
    _blendBindGroupLayout = null;
    _blendTexBindGroup = null;
  };

}